import type { GetBittensorNativeBalancesOutput } from 'clients/api';
import type { Token } from 'types';
import { areTokensEqual } from 'utilities';

import type { VTokenWrapperKind } from './getVTokenWrapperKind';

export interface MarketCapabilities {
  wrapperKind: VTokenWrapperKind;
  canUseNativeTao: boolean;
  canUseNativeAlpha: boolean;
  canRouteNativeBorrow: boolean;
  canRouteNativeRepay: boolean;
  canUnwrapNativeCollateral: boolean;
}

export interface GetMarketCapabilitiesInput {
  underlyingToken: Token;
  wrapperKind: VTokenWrapperKind;
  bittensorNativeBalances?: GetBittensorNativeBalancesOutput;
}

const hasMatchingNativeAlphaBalance = ({
  underlyingToken,
  bittensorNativeBalances,
}: Pick<GetMarketCapabilitiesInput, 'underlyingToken' | 'bittensorNativeBalances'>) =>
  !!bittensorNativeBalances?.alphaBalances.some(
    (alphaBalance) =>
      alphaBalance.wrapperAddress.toLowerCase() === underlyingToken.address.toLowerCase() ||
      areTokensEqual(alphaBalance.wrapperToken, underlyingToken),
  );

export const getMarketCapabilities = ({
  underlyingToken,
  wrapperKind,
  bittensorNativeBalances,
}: GetMarketCapabilitiesInput): MarketCapabilities => {
  const canUseNativeTao = wrapperKind === 'wsTAO' && !!underlyingToken.tokenWrapped;
  const canUseNativeAlpha =
    wrapperKind === 'wAlpha' &&
    hasMatchingNativeAlphaBalance({ underlyingToken, bittensorNativeBalances });
  const canRouteNativeTao = wrapperKind === 'wsTAO';
  const canUnwrapNativeCollateral = wrapperKind === 'wsTAO' || wrapperKind === 'wAlpha';

  return {
    wrapperKind,
    canUseNativeTao,
    canUseNativeAlpha,
    canRouteNativeBorrow: canRouteNativeTao,
    // wsTAO-only by design: its sole consumer is the bundled close-position
    // gate (canBundleClosePosition in RepayWithWalletBalanceForm), and wAlpha
    // bundle-close is not wired. Plain native-alpha repay still routes through
    // useRouterFlow regardless of this flag — do NOT read this as "native
    // repay is supported".
    canRouteNativeRepay: canRouteNativeTao,
    canUnwrapNativeCollateral,
  };
};
